import { TreeNodeDTO } from 'business/models/Models';
import {
  ArrowKeys,
  ExplorerFocusArea,
  HorizontalArrowKeys,
  KeyboardKeys,
  VerticalArrowKeys,
} from 'constants/';
import { useContainerFocus } from 'CustomHooks/useContainerFocus';
import { useEventListener } from 'CustomHooks/useEventListener';
import { useState, useCallback, useContext } from 'react';
import { isCtrlKeyPressed } from 'utils/Global';
import { getNormalizedKeyboardKey } from './utils';
import { debounce } from 'lodash';
import TreeContext from 'components/main/tree/TreeContext';

interface Props {
  onF5Press?: (e: KeyboardEvent) => Promise<void>;
  onDeletePress?: () => Promise<void>;
  onCtrlAPress?: () => void;
  nodesParentContainerRef?: React.RefObject<HTMLDivElement>;
  selectedNodes?: TreeNodeDTO[];
  selectNodesById: (nodeIds: number[]) => void;
  rmLite?: boolean;
  focusedArea?: ExplorerFocusArea;
  setFocusedArea?: React.Dispatch<React.SetStateAction<ExplorerFocusArea>>;
}

const getColumnCount = (items: HTMLElement[]) => {
  const firstTop = items[0].getBoundingClientRect().top;
  return (
    items.findIndex((item) => item.getBoundingClientRect().top !== firstTop) ||
    items.length
  );
};

export const handleArrowKeyNavigation = (
  nodesParentContainerRef: React.RefObject<HTMLDivElement>,
  selectedNodes: TreeNodeDTO[],
  selectNodesById,
  key: string,
  enableCircularNavigation: boolean = false,
): any | undefined => {
  const container = nodesParentContainerRef.current;
  if (!container) {
    return;
  }

  const items = Array.from(container.querySelectorAll<HTMLElement>('.selectable-item'));
  if (items.length === 0) {
    return;
  }

  const colCount = getColumnCount(items);

  const selectedIndex = items.findIndex((item) => {
    const nodeId = parseInt(item.dataset.id || '');
    return nodeId === selectedNodes?.[0]?.id;
  });

  if (selectedIndex === -1) {
    const newItem = items[0];
    const newId = parseInt(newItem.dataset.id || '');
    if (newId) {
      selectNodesById([newId]);
    }
    return;
  }

  let newIndex = selectedIndex;

  switch (key) {
    case 'ArrowRight':
      newIndex = selectedIndex + 1;
      if (newIndex >= items.length) {
        newIndex = enableCircularNavigation ? 0 : selectedIndex;
      }
      break;

    case 'ArrowLeft':
      newIndex = selectedIndex - 1;
      if (newIndex < 0) {
        newIndex = enableCircularNavigation ? items.length - 1 : selectedIndex;
      }
      break;

    case 'ArrowDown':
      newIndex = selectedIndex + colCount;
      if (newIndex >= items.length) {
        newIndex = enableCircularNavigation ? selectedIndex % colCount : selectedIndex;
      }
      break;

    case 'ArrowUp':
      newIndex = selectedIndex - colCount;
      if (newIndex < 0) {
        newIndex = enableCircularNavigation
          ? (items.length - colCount + (selectedIndex % colCount)) % items.length
          : selectedIndex;
      }
      break;

    default:
      return;
  }

  const newItem = items[newIndex];
  const newId = parseInt(newItem.dataset.id || '');
  newItem.scrollIntoView({
    block: 'nearest',
  });
  selectNodesById([newId]);
};

export const useExplorerKeyboardHandler = (props: Props) => {
  const {
    onF5Press,
    onDeletePress,
    onCtrlAPress,
    nodesParentContainerRef,
    selectedNodes,
    selectNodesById,
    rmLite,
    focusedArea,
    setFocusedArea,
  } = props;
  const [isControlKeyPressed, setIsControlKeyPressed] = useState<boolean>(false);
  const [isShiftKeyPressed, setIsShiftKeyPressed] = useState<boolean>(false);

  const onFocusChange = (isFocused) => {
    if (isFocused) {
      setFocusedArea(ExplorerFocusArea.ChildrenView);
    } else {
      if (focusedArea === ExplorerFocusArea.ChildrenView) {
        setFocusedArea(ExplorerFocusArea.None);
      }
    }
  };
  const [isFocused] = useContainerFocus(nodesParentContainerRef, false, onFocusChange);

  const handleKeyDown = useCallback(
    (e) => {
      const key = getNormalizedKeyboardKey(e);
      if (e.ctrlKey && key === KeyboardKeys.F) {
        e.preventDefault();
        window.open('/search', '_blank');
      }

      if (key === KeyboardKeys.F5) {
        e.preventDefault();
      }

      setIsControlKeyPressed(isCtrlKeyPressed(e));

      if (e.shiftKey) {
        setIsShiftKeyPressed(true);
      }

      if (!isFocused) {
        return;
      }
      if (ArrowKeys.includes(key)) {
        e.preventDefault();
        handleArrowKeyNavigation(
          nodesParentContainerRef,
          selectedNodes,
          selectNodesById,
          key,
        );
      }

      if (e.ctrlKey && key === KeyboardKeys.A) {
        e.preventDefault();
        onCtrlAPress();
      } else if (key === KeyboardKeys.Tab) {
        e.preventDefault();
        handleArrowKeyNavigation(
          nodesParentContainerRef,
          selectedNodes,
          selectNodesById,
          KeyboardKeys.ArrowRight,
          true,
        );
      }
    },
    [isFocused, selectedNodes, selectNodesById, nodesParentContainerRef],
  );

  const handleKeyUp = useCallback(
    async (e) => {
      if (!isFocused) {
        return;
      }
      const key = getNormalizedKeyboardKey(e);

      setIsControlKeyPressed(isCtrlKeyPressed(e));
      setIsShiftKeyPressed(false);

      if (key === KeyboardKeys.F5) {
        e.preventDefault();
        document.body.style.cursor = 'wait';

        if (onF5Press) {
          await onF5Press(e);
        }

        document.body.style.cursor = 'default';
      }

      if (key === KeyboardKeys.Delete && !rmLite) {
        e.preventDefault();
        onDeletePress();
      }
    },
    [onF5Press, onDeletePress, isFocused],
  );

  useEventListener('keydown', handleKeyDown);
  useEventListener('keyup', handleKeyUp);

  return { isControlKeyPressed, isShiftKeyPressed };
};
interface TreeKeyboardHandlerProps {
  treeContainerRef: React.RefObject<HTMLDivElement>;
  selectedExplorerNode: any;
  treenodes: any[];
  onSelectNode: (node: any) => void;
  onToggleExpand: (
    node: any,
    expandAndSelect: boolean,
    expandOnSelection?: boolean,
  ) => void;
  getTreeNodeById: (nodes: any[], id: number) => any;
  onCtrlAPress: () => void;
}
const debouncedExpand = debounce(
  (
    node: any,
    onToggleExpand: (
      n: any,
      expandAndSelect: boolean,
      expandOnSelection: boolean,
    ) => void,
  ) => {
    if (!node.state?.expanded) {
      onToggleExpand(node, true, false);
    }
  },
  200,
);
function handleTreeArrowKeyNavigation(
  key: KeyboardKeys,
  container: HTMLElement,
  selectedExplorerNode: { id: number } | null,
  treenodes: TreeNodeDTO[], // adjust type accordingly
  getTreeNodeById: (nodes: any[], id: number) => any,
  onSelectNode: (node: TreeNodeDTO) => void,
  onToggleExpand: (node: TreeNodeDTO, expand: boolean) => void,
  enableCircularNavigation: boolean = false,
) {
  if (!ArrowKeys.includes(key)) {
    return;
  }

  const nodeElements = Array.from(
    container.getElementsByClassName('explorer-nodes'),
  ) as HTMLElement[];

  if (!nodeElements.length || !selectedExplorerNode) {
    return;
  }

  const selectedId = selectedExplorerNode.id;
  const currentIndex = nodeElements.findIndex(
    (el) => parseInt(el.getAttribute('data-testid') || '', 10) === selectedId,
  );

  if (currentIndex === -1) {
    return;
  }

  let newIndex = currentIndex;

  if (key === KeyboardKeys.ArrowDown) {
    newIndex = currentIndex + 1;
    if (newIndex >= nodeElements.length) {
      newIndex = enableCircularNavigation ? 0 : nodeElements.length - 1;
    }
  } else if (key === KeyboardKeys.ArrowUp) {
    newIndex = currentIndex - 1;
    if (newIndex < 0) {
      newIndex = enableCircularNavigation ? nodeElements.length - 1 : 0;
    }
  }

  const newElement = nodeElements[newIndex];
  const newTreeNodeId = parseInt(newElement?.getAttribute('data-testid') || '', 10);
  if (isNaN(newTreeNodeId)) {
    return;
  }

  if (VerticalArrowKeys.includes(key as KeyboardKeys)) {
    const targetNode = getTreeNodeById(treenodes, newTreeNodeId);
    if (targetNode) {
      onSelectNode(targetNode);
      debouncedExpand(targetNode, onToggleExpand);
    }
  } else if (HorizontalArrowKeys.includes(key as KeyboardKeys)) {
    const selectedNode = getTreeNodeById(treenodes, selectedId);
    if (!selectedNode || !selectedNode?.hasContainerNode) {
      return;
    }

    if (key === KeyboardKeys.ArrowRight && !selectedNode.state?.expanded) {
      onToggleExpand(selectedNode, true);
    } else if (key === KeyboardKeys.ArrowLeft && selectedNode.state?.expanded) {
      onToggleExpand(selectedNode, false);
    }
  }

  newElement?.scrollIntoView?.({ block: 'nearest' });
}

export const useTreeKeyboardHandler = ({
  treeContainerRef,
  selectedExplorerNode,
  treenodes,
  onSelectNode,
  onToggleExpand,
  getTreeNodeById,
  onCtrlAPress,
}: TreeKeyboardHandlerProps) => {
  const treeContext = useContext(TreeContext);

  const onFocusChange = (isFocused) => {
    if (isFocused) {
      treeContext?.setFocusedArea(ExplorerFocusArea.TreeView);
    } else {
      if (treeContext.focusedArea === ExplorerFocusArea.TreeView) {
        treeContext?.setFocusedArea(ExplorerFocusArea.None);
      }
    }
  };
  const [isFocused] = useContainerFocus(treeContainerRef, false, onFocusChange);
  useEventListener('keydown', (event: KeyboardEvent) => {
    const container = treeContainerRef?.current;
    if (!isFocused || !container) {
      return;
    }
    const key = getNormalizedKeyboardKey(event);

    if (ArrowKeys.includes(key)) {
      event.preventDefault();
      handleTreeArrowKeyNavigation(
        key,
        container,
        selectedExplorerNode,
        treenodes,
        getTreeNodeById,
        onSelectNode,
        onToggleExpand,
      );
    } else if (event.ctrlKey && key === KeyboardKeys.A && onCtrlAPress) {
      event.preventDefault();
      onCtrlAPress();
    } else if (key === KeyboardKeys.Tab) {
      event.preventDefault();
      handleTreeArrowKeyNavigation(
        KeyboardKeys.ArrowDown,
        container,
        selectedExplorerNode,
        treenodes,
        getTreeNodeById,
        onSelectNode,
        onToggleExpand,
        true,
      );
    }
  });
};
